import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { getRun } from "@/services/api"
import type { BackendRunDetail } from "@/types"
import { Panel, Stat, StatGrid, Badge } from "@/components/ui"
export default function RunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [run, setRun] = useState<BackendRunDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    if (!id) return
    getRun(id)
      .then((res) => {
        if (!cancelled) setRun(res.data)
      })
      .catch(() => {
        if (!cancelled) setRun(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-muted">
        <p>Loading run&hellip;</p>
      </div>
    )
  }
  if (!run) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-muted">
        <p>Run not found.</p>
      </div>
    )
  }

  const m = run.metrics || {}
  const rate = m.landing_success === true ? 100 : 0

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/runs")}
            className="p-1.5 text-dim hover:text-ink hover:bg-surface rounded-sm transition"
            aria-label="Back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink tracking-tight">Run {shortId(run.id)}</h1>
            <p className="text-muted text-sm">
              {run.algorithm.toUpperCase()} &middot; {run.obs_mode ?? "n/a"} obs &middot; seed{" "}
              {run.seed ?? "—"}
            </p>
          </div>
        </div>
        <Badge variant={rate >= 50 ? "safe" : "danger"}>
          {m.landing_success === true ? "LANDED" : "IMPACT"}
        </Badge>
      </div>

      <StatGrid cols={5}>
        <Stat label="Algorithm" value={run.algorithm.toUpperCase()} unit={run.obs_mode ?? ""} status="nominal" accent="blue" />
        <Stat label="Seed" value={run.seed ?? "—"} status="nominal" accent="cyan" />
        <Stat label="Status" value={run.status} status={run.status === "completed" ? "safe" : "running"} accent="amber" />
        <Stat label="Progress" value={run.progress?.toFixed?.(0) ?? "0"} unit="%" status="nominal" accent="cyan" />
        <Stat label="Result" value={m.landing_success === true ? "Landed" : "Impacted"} status={m.landing_success === true ? "safe" : "danger"} accent="amber" />
      </StatGrid>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 xl:col-span-8 gap-4">
          <Panel title="Flight outcome" padded={false}>
            <div className="p-3 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Touchdown velocity</span>
                <span className="font-mono text-ink">{m.touchdown_velocity_mps?.toFixed?.(1) ?? "—"} m/s</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Horizontal error</span>
                <span className="font-mono text-ink">{m.horizontal_error_m?.toFixed?.(1) ?? "—"} m</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Fuel used</span>
                <span className="font-mono text-ink">{m.fuel_used_kg?.toFixed?.(1) ?? "—"} kg</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Fuel remaining</span>
                <span className="font-mono text-ink">{m.fuel_remaining_kg?.toFixed?.(1) ?? "—"} kg</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Max altitude</span>
                <span className="font-mono text-ink">{m.max_altitude_m?.toFixed?.(0) ?? "—"} m</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Duration</span>
                <span className="font-mono text-ink">{m.duration_s?.toFixed?.(1) ?? "—"} s</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted">Start altitude</span>
                <span className="font-mono text-ink">{m.start_altitude_m?.toFixed?.(0) ?? "—"} m</span>
              </div>
            </div>
          </Panel>
        </div>

        <div className="lg:col-span-5 xl:col-span-4 gap-4">
          <Panel title="Telemetry &amp; logs" padded={false}>
            <div className="p-3">
              <div className="text-xs text-muted uppercase tracking-wider mb-2">
                Run {shortId(run.id)} &middot; {run.algorithm.toUpperCase()}
              </div>
              <div className="text-sm text-muted">
                {run.status === "completed"
                  ? "Run completed at " + new Date(run.completed_at ?? run.created_at).toLocaleString()
                  : run.status === "running"
                  ? "Run in progress"
                  : run.status === "failed"
                  ? "Run failed: " + (run.error_message ?? "unknown error")
                  : "Run status: " + run.status}
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  )
}

function shortId(id: string, len = 8): string {
  return id.slice(0, len)
}
